import { useState } from "react";
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import { RichTextEditor } from "@/components/email/RichTextEditor";
import { errorMessage } from "@/api/emailThreads";
import {
  createSignature, deleteSignature, fetchSignatures, updateSignature, type EmailSignature,
} from "@/api/signatures";

// Rendered signature preview. The HTML is the user's own and was sanitized by
// the server when saved.
function SignaturePreview({ html }: { html: string }) {
  return (
    <Box
      sx={{ fontSize: 14, lineHeight: 1.5, "& p": { my: 0.25 }, "& img": { maxWidth: "100%", height: "auto" } }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SignatureDialog({ signature, onClose }: { signature: EmailSignature | "new"; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = signature === "new";
  const [name, setName] = useState(isNew ? "" : signature.name);
  const [html, setHtml] = useState(isNew ? "" : signature.body_html);
  const [empty, setEmpty] = useState(isNew);
  const [editorError, setEditorError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => (isNew
      ? createSignature({ name, body_html: html })
      : updateSignature(signature.id, { name, body_html: html })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-signatures"] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isNew ? "New signature" : "Edit signature"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Name"
            placeholder="e.g. Default, Short, Vendor follow-up"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            autoFocus={isNew}
            size="small"
          />
          <RichTextEditor
            initialHtml={isNew ? undefined : signature.body_html}
            placeholder="Your name, title, phone… paste or drop a logo to add it"
            minHeight={140}
            onChange={(h, isEmpty) => { setHtml(h); setEmpty(isEmpty); }}
            onError={setEditorError}
          />
          {editorError && <Alert severity="warning" onClose={() => setEditorError(null)}>{editorError}</Alert>}
          {save.isError && <Alert severity="error">{errorMessage(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || empty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ProfilePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const signatures = useQuery({ queryKey: ["email-signatures"], queryFn: fetchSignatures });
  const [editing, setEditing] = useState<EmailSignature | "new" | null>(null);
  const [deleting, setDeleting] = useState<EmailSignature | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: ["email-signatures"] });
  const makeDefault = useMutation({ mutationFn: (id: number) => updateSignature(id, { default: true }), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (id: number) => deleteSignature(id),
    onSuccess: () => { setDeleting(null); refresh(); },
  });

  if (!user) return null;

  return (
    <AppLayout maxWidth="md">
      <Typography variant="h5" gutterBottom>Profile</Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={user.avatar_url ?? undefined} sx={{ width: 56, height: 56 }}>{user.email[0]?.toUpperCase()}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{user.name || user.email}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
            </Box>
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Email replies:</strong>{" "}
              {user.email_responder ? "On. You can reply to AP emails from FP Accounting." : "Off. Ask an admin to turn on Email replies for you."}
            </Typography>
            {user.email_responder && (
              <Typography variant="body2">
                <strong>Send as yourself:</strong>{" "}
                {user.can_reply_as_self
                  ? "Available. Replies go out from your own Gmail through the company Google account, so there's nothing to link."
                  : "Not available for this account. You can reply as the AP inbox."}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Email signatures</Typography>
          <Typography variant="body2" color="text.secondary">
            Your default signature is added to every reply, whether it goes out as the AP inbox or as you. You can switch or remove it in the reply window.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing("new")}>New signature</Button>
      </Stack>

      {signatures.isLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>}
      {signatures.isError && <Alert severity="error">{errorMessage(signatures.error)}</Alert>}
      {(makeDefault.isError || remove.isError) && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(makeDefault.error ?? remove.error)}</Alert>}

      <Stack spacing={1.5}>
        {signatures.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">No signatures yet. Replies go out unsigned until you add one.</Typography>
        )}
        {signatures.data?.map((s) => (
          <Card key={s.id} variant="outlined">
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                {s.default && <Chip size="small" color="primary" label="Default" />}
                <Box sx={{ flex: 1 }} />
                {!s.default && (
                  <Tooltip title="Make default">
                    <IconButton size="small" onClick={() => makeDefault.mutate(s.id)} disabled={makeDefault.isPending} aria-label={`Make ${s.name} the default`}>
                      <StarBorderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {s.default && <StarIcon fontSize="small" color="primary" />}
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditing(s)} aria-label={`Edit ${s.name}`}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleting(s)} aria-label={`Delete ${s.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
              <Box sx={{ borderLeft: 3, borderColor: "divider", pl: 1.5 }}>
                <SignaturePreview html={s.body_html} />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {editing && <SignatureDialog signature={editing} onClose={() => setEditing(null)} />}

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete “{deleting?.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleting?.default ? "It's your default; your next signature becomes the default. " : ""}This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => deleting && remove.mutate(deleting.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
